import React, { useState } from 'react'
import type { AuthMode } from '../../types/auth'
import GoogleAuthButton from './GoogleAuthButton'
import { useAmplitude } from '../../hooks/useAmplitude'
import { AmplitudeEvents } from '../../types/amplitude'
import type { AuthEventProperties } from '../../types/amplitude'

interface AuthFormProps {
  mode: AuthMode
  onModeChange: (mode: AuthMode) => void
  onSubmit: (data: { email: string; password: string; confirmPassword?: string }) => Promise<void>
  onGoogleAuth: () => Promise<void>
  isLoading: boolean
  error: string | null
}

const AuthForm: React.FC<AuthFormProps> = ({
  mode,
  onModeChange,
  onSubmit,
  onGoogleAuth,
  isLoading,
  error,
}) => {
  const { track } = useAmplitude()
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
  })

  const [fieldErrors, setFieldErrors] = useState<{
    email?: string
    password?: string
    confirmPassword?: string
  }>({})

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))

    // Clear field error when user starts typing
    if (fieldErrors[name as keyof typeof fieldErrors]) {
      setFieldErrors((prev) => ({ ...prev, [name]: undefined }))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Basic validation
    const errors: typeof fieldErrors = {}

    if (!formData.email) {
      errors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address'
    }

    if (!formData.password) {
      errors.password = 'Password is required'
    } else if (formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters long'
    }

    if (mode === 'signup') {
      if (!formData.confirmPassword) {
        errors.confirmPassword = 'Please confirm your password'
      } else if (formData.password !== formData.confirmPassword) {
        errors.confirmPassword = 'Passwords do not match'
      }
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors)
      return
    }

    // Track the event
    const eventProperties: AuthEventProperties = {
      url: window.location.href,
      description: 'simple-landing-page-test',
      authMethod: 'email',
      timestamp: new Date().toISOString(),
    }

    const eventName =
      mode === 'signup' ? AmplitudeEvents.CREATE_ACCOUNT_PRESSED : AmplitudeEvents.SIGN_IN_PRESSED

    track(eventName, eventProperties)

    await onSubmit(formData)
  }

  const toggleMode = () => {
    onModeChange(mode === 'signup' ? 'login' : 'signup')
    setFieldErrors({})
  }

  return (
    <div className="auth-form-container">
      <div className="auth-form-header">
        <h2 id="auth-modal-title" className="auth-form-title">
          {mode === 'signup' ? 'Create your account' : 'Welcome back'}
        </h2>
        <p className="auth-form-subtitle">
          {mode === 'signup' ? (
            <>
              Configure and deploy your Ask Anything
              <br />
              site search today!
            </>
          ) : (
            'Sign in to your account to continue'
          )}
        </p>
      </div>

      <div className="auth-form-content">
        <GoogleAuthButton mode={mode} onGoogleAuth={onGoogleAuth} isLoading={isLoading} />

        <div className="auth-form-divider">
          <div className="auth-form-divider-line"></div>
          <span className="auth-form-divider-text">or</span>
          <div className="auth-form-divider-line"></div>
        </div>

        {error && <div className="auth-form-error">{error}</div>}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="auth-form-field">
            <label htmlFor="email" className="auth-form-label">
              Email address
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={`auth-form-input ${fieldErrors.email ? 'auth-form-input-error' : ''}`}
              placeholder="Enter your email"
              disabled={isLoading}
              autoComplete="email"
            />
            {fieldErrors.email && (
              <span className="auth-form-field-error">{fieldErrors.email}</span>
            )}
          </div>

          <div className="auth-form-field">
            <label htmlFor="password" className="auth-form-label">
              Password
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              className={`auth-form-input ${fieldErrors.password ? 'auth-form-input-error' : ''}`}
              placeholder={mode === 'signup' ? 'Create a password' : 'Enter your password'}
              disabled={isLoading}
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
            />
            {fieldErrors.password && (
              <span className="auth-form-field-error">{fieldErrors.password}</span>
            )}
          </div>

          {mode === 'signup' && (
            <div className="auth-form-field">
              <label htmlFor="confirmPassword" className="auth-form-label">
                Confirm password
              </label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                className={`auth-form-input ${fieldErrors.confirmPassword ? 'auth-form-input-error' : ''}`}
                placeholder="Confirm your password"
                disabled={isLoading}
                autoComplete="new-password"
              />
              {fieldErrors.confirmPassword && (
                <span className="auth-form-field-error">{fieldErrors.confirmPassword}</span>
              )}
            </div>
          )}

          <button type="submit" disabled={isLoading} className="auth-form-submit">
            {isLoading
              ? mode === 'signup'
                ? 'Creating account...'
                : 'Signing in...'
              : mode === 'signup'
                ? 'Create account'
                : 'Sign in'}
          </button>
        </form>

        <div className="auth-form-switch">
          <span className="auth-form-switch-text">
            {mode === 'signup' ? 'Already have an account?' : "Don't have an account?"}
          </span>
          <button
            type="button"
            onClick={toggleMode}
            className="auth-form-switch-link"
            disabled={isLoading}
          >
            {mode === 'signup' ? 'Sign in' : 'Sign up'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default AuthForm
